import { useCallback, useMemo, useState } from 'react';

import {
  EmptyState,
  ErrorState,
  Footer,
  Header,
  LoadingState,
  RefreshingState,
} from './components/Chrome';
import { FilterBar } from './components/FilterBar';
import { GroupCard } from './components/GroupCard';
import { GroupDetail } from './components/GroupDetail';
import type { Group } from './lib/types';
import { applyFilters, useFilters } from './lib/useFilters';
import { useGroupData } from './lib/useGroupData';

export default function App() {
  const state = useGroupData();
  const { filters, toggle, toggleDay, setKidsMinistry, setQuery, clear, activeCount } =
    useFilters();
  const [selected, setSelected] = useState<Group | null>(null);

  const groups = state.status === 'ready' ? state.data.groups : [];
  const visible = useMemo(() => applyFilters(groups, filters), [groups, filters]);

  const closeDetail = useCallback(() => setSelected(null), []);

  const site = state.status === 'ready' ? state.data.meta.site : {};
  const photoStyle = site.photoStyle ?? 'banner';
  // Two terms are listed while one winds down and the next opens, and groups
  // repeat across them — so cards need to say which term they belong to.
  const showTerm = state.status === 'ready' && (state.data.meta.terms?.length ?? 0) > 1;

  return (
    <>
      <a className="skip-link" href="#groups">
        Skip to groups
      </a>

      <Header title={site.title} tagline={site.tagline} howTo={site.howTo} />

      <main id="groups" className="shell">
        {state.status === 'loading' && <LoadingState />}
        {state.status === 'refreshing' && <RefreshingState />}
        {state.status === 'error' && <ErrorState message={state.message} />}

        {state.status === 'ready' && (
          <>
            <FilterBar
              facets={state.data.facets}
              filters={filters}
              onToggle={toggle}
              onToggleDay={toggleDay}
              onKidsMinistry={setKidsMinistry}
              onQuery={setQuery}
              onClear={clear}
              activeCount={activeCount}
              resultCount={visible.length}
            />

            {visible.length === 0 ? (
              <EmptyState onClear={clear} query={filters.query} />
            ) : (
              <ul className="grid">
                {visible.map((group) => (
                  <li key={group.id} id={`group-${group.id}`}>
                    <GroupCard
                      group={group}
                      onOpen={setSelected}
                      showTerm={showTerm}
                      photoStyle={photoStyle}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </main>

      <GroupDetail
        group={selected}
        onClose={closeDetail}
        showTerm={showTerm}
        photoStyle={photoStyle}
      />

      <Footer
        note={site.signupNote}
        updatedAt={state.status === 'ready' ? state.data.meta.generatedAt : null}
      />
    </>
  );
}
